import {
	AvailableAttributes,
	CreatorState,
	CreatorStep,
	SerializedCreator,
} from '@/types/creator';
import { Relation, RelationAction, RelationType, UUID } from '@/types/types';
import { AttributeValue } from '@/api/AttributeValueApi';
import { WritableDraft } from '@reduxjs/toolkit';
import { Product } from '@/api/ProductApi';
import { ContentContext } from '@/providers/ContentProvider';
import { PricingMethod, ProductType } from '@/api/ProductTypeApi';

export function isProductOutsideWarranty(
	productType: ProductType | null,
	width?: number,
	height?: number
): boolean {
	if (!productType) return false;
	if (!width || !height) return false;

	const { widthOutsideWarranty, heightOutsideWarranty } = productType;

	if (widthOutsideWarranty != null && width > widthOutsideWarranty) {
		return true;
	}

	if (heightOutsideWarranty != null && height > heightOutsideWarranty) {
		return true;
	}

	return false;
}

export function isCreatorConfigurationComplete(state: CreatorState): boolean {
	const { productType, creatorProduct, selectedValues, selectedFabric } = state;

	if (!productType) return false;

	if (!creatorProduct.dimensions.width || !creatorProduct.dimensions.height) {
		return false;
	}

	if (
		productType.widthInputModes.length > 0 &&
		!creatorProduct.widthInputMode
	) {
		return false;
	}

	if (
		productType.heightInputModes.length > 0 &&
		!creatorProduct.heightInputMode
	) {
		return false;
	}

	if (productType.pricingMethod === PricingMethod.PER_M2 && !selectedFabric) {
		return false;
	}

	for (const requiredAttributeId of productType.requiredAttributeAnswersIds) {
		if (!selectedValues[requiredAttributeId]) return false;
	}

	return true;
}

export function setAttributesAvailability(state: WritableDraft<CreatorState>) {
	const valuesSet = new Set<UUID>(
		Object.values(state.selectedValues)
			.filter(Boolean)
			.map((val) => val!.id)
	);

	for (const creatorAttribute of state.availableAttributes) {
		creatorAttribute.filteredValues = [];

		for (const attributeValue of creatorAttribute.attributeValues) {
			if (!attributeValue.relations.length) {
				creatorAttribute.filteredValues.push(attributeValue);
				continue;
			}

			const isRelationsFullfiled = attributeValue.relations.every((relation) =>
				isRelationFullfiled(relation, valuesSet)
			);

			if (isRelationsFullfiled)
				creatorAttribute.filteredValues.push(attributeValue);
		}
	}
}

export function unSelectUnavailableAttributeValues(
	state: WritableDraft<CreatorState>
) {
	let attributeId: UUID;
	for (attributeId in state.selectedValues) {
		const selectedValue = state.selectedValues[attributeId];
		if (!selectedValue) continue;

		const isAvailable = state.availableAttributes
			.find((attribute) => attribute.id === attributeId)
			?.filteredValues.some((value) => value.id === selectedValue.id);

		if (!isAvailable) state.selectedValues[attributeId] = null;
	}
}

function isRelationFullfiled(relation: Relation, selectedValues: Set<UUID>) {
	let match = false;
	switch (relation.type) {
		case RelationType.ATTRIBUTE_VALUE:
			match = selectedValues.has(relation.objectId);
			break;
	}

	return match !== (relation.action === RelationAction.HIDE);
}

export function formatAttributeValueName(
	attributeValue: AttributeValue,
	getEntityText: ContentContext['getEntityText']
) {
	const displayName = getEntityText(attributeValue, 'displayName');
	if (!attributeValue.attribute?.suffix)
		return displayName || attributeValue.value;

	return `${displayName || attributeValue.value} ${attributeValue.attribute.suffix}`.trim();
}

export function serializeCreator(state: CreatorState): SerializedCreator {
	return {};
}

export function loadProduct(
	state: WritableDraft<CreatorState>,
	product: Product
) {}

export function mapCreatorStepName(step: CreatorStep, isMobile?: boolean) {
	if (isMobile) return '';

	switch (step) {
		case CreatorStep.CONFIGURATION:
			return 'Konfiguracja';
		case CreatorStep.ACCESSORIES:
			return 'Akcesoria';
		case CreatorStep.SUMMARY:
			return 'Podsumowanie';
	}
}

export function getAvailableAttributes(productType: ProductType) {
	const availableAttributes: AvailableAttributes = [];

	for (const attributeValue of productType.availableAttributeValues) {
		if (!attributeValue.attribute) continue;

		const foundAttribute = availableAttributes.find(
			(attribute) => attribute.id === attributeValue.attributeId
		);

		if (foundAttribute) {
			foundAttribute.attributeValues.push(attributeValue);
			continue;
		}

		availableAttributes.push({
			...attributeValue.attribute,
			attributeValues: [attributeValue],
			filteredValues: [],
		});
	}

	availableAttributes.sort((a, b) => a.listOrder - b.listOrder);

	return availableAttributes;
}
